/**
 * A single day within the agenda's currently loaded window, replacing the page's old fixed
 * "hoje" label now that a window can reach back further than today. Bounded by the read's own
 * `earliest`/`latest` -- never a locally invented range -- so a day the ledger cannot answer for
 * is refused rather than offered and then failed against.
 */

import type { ReactNode } from 'react';

import { Input } from '../../../ui/input';
import type { Window } from './api/queries';
import { startOfDay } from './dayHeading';
import { cm } from './messages';

const DAY_MS = 24 * 3600_000;

export function dayWindow(day: Date): Window {
  const start = startOfDay(day);
  return { from: start.toISOString(), to: new Date(start.getTime() + DAY_MS).toISOString() };
}

function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseIsoDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

export function DayPicker(props: {
  value: Date;
  onChange: (day: Date) => void;
  min: Date | null;
  max: Date | null;
  disabled?: boolean;
}): ReactNode {
  const min = props.min !== null ? startOfDay(props.min) : null;
  const max = props.max !== null ? startOfDay(props.max) : null;

  return (
    <Input
      type="date"
      aria-label={cm('agenda.dayPicker.label')}
      className="h-9 w-auto"
      value={isoDate(props.value)}
      min={min !== null ? isoDate(min) : undefined}
      max={max !== null ? isoDate(max) : undefined}
      disabled={props.disabled}
      onChange={(event) => {
        if (event.target.value === '') return;
        const day = startOfDay(parseIsoDate(event.target.value));
        if (Number.isNaN(day.getTime())) return;
        // `min`/`max` keep a real browser's own picker UI from offering an out-of-range day;
        // a browser still accepts one typed by hand, so this guard -- not the attributes
        // above -- is what actually refuses one here.
        if (min !== null && day.getTime() < min.getTime()) return;
        if (max !== null && day.getTime() > max.getTime()) return;
        props.onChange(day);
      }}
    />
  );
}
